import { type ILiveValuesTransformEvent } from '@framework/validation/live/LiveTypes.ts';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormAugmentedContext } from '../form/augmented/FormAugmentedContext.tsx';
import { syncComputedValues } from './syncComputedValues.ts';

/**
 * Schedula `syncComputedValues` nella prossima macro task (setTimeout).
 *
 * La dedup è globale per form: il ref `computedSyncScheduled` è
 * condiviso tramite `FormAugmentedContext`, quindi chiamate multiple
 * da hook/componenti diversi nello stesso ciclo sincrono producono
 * un solo `syncComputedValues`.
 *
 * Usa `setTimeout(fn, 0)` (macro task) invece di `queueMicrotask`
 * per cedere il controllo al browser e permettere il paint del keystroke
 * PRIMA che `schema.cast()` (~30ms) blocchi il main thread.
 *
 * Utilizzare dopo `field.onChange()` da `useController` o in qualsiasi
 * punto dove si modifica il form state senza passare per `useFormSetValue`.
 */
export function useTriggerComputedSync() {
  const methods = useFormContext();
  const augmented = useFormAugmentedContext();

  return useCallback(
    (event?: ILiveValuesTransformEvent) => {
      if (!augmented) {
        // guard difensivo: no-op senza FormAugmentedContext
        console.warn(
          'useTriggerComputedSync called without FormAugmentedContext',
        );
        return;
      }
      if (event) {
        const queue = augmented.computedSyncEvents.current;
        // Accoda con dedup per path: un path ri-modificato viene rimosso e
        // riaccodato in fondo, così vince l'ultimo valore e la coda resta
        // corta anche con typing veloce sullo stesso campo.
        const existingIndex = queue.findIndex(
          queued => queued.path === event.path,
        );
        if (existingIndex !== -1) {
          queue.splice(existingIndex, 1);
        }
        queue.push(event);
      }
      if (augmented.computedSyncScheduled.current) return;
      augmented.computedSyncScheduled.current = true;
      augmented.resolvedStore.isStale = true; // flag "stale" per i subscriber allo store
      // setTimeout(0) → macro task → il browser painta prima del cast
      setTimeout(() => {
        augmented.computedSyncScheduled.current = false;
        const syncEvents = augmented.computedSyncEvents.current;
        augmented.computedSyncEvents.current = [];

        // ===
        // A) Calcolo dei valori computed tramite `schema.cast()`
        syncComputedValues(
          methods,
          augmented.schema,
          augmented.validationContext,
          augmented.initialValues,
          syncEvents,
        );

        // ===
        // B) Aggiorna gli schema risolti con i valori post-computed
        const currentValues = methods.getValues();

        // B1) Risolve gli schema con i nuovi valori, così che i campi figli
        // abbiano accesso ai nuovi state (es. per visibilità/editabilità).
        // `resolvedStore.resolve` estrae internamente il context live dai meta
        // dello schema partendo dal validationContext raw.
        augmented.resolvedStore.resolve(
          augmented.schema,
          currentValues,
          augmented.validationContext,
        );

        // B2) Notifica i subscriber dopo il commit DOM, prima del paint. Qui
        // siamo in setTimeout, quindi non c'è rischio di state-update durante
        // il render, a differenza di `FormAugmentedContext.ts`
        augmented.resolvedStore.flushNotifications();
      });
    },
    [methods, augmented],
  );
}
